import { useRef, useEffect, useContext, useMemo, useCallback } from 'react';
import uuid from 'uuid/v4';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { CollabEditPlugin } from '@atlaskit/editor-plugin-collab-edit/collabEditPluginType';
import type { SyncedBlockPlugin } from '@atlaskit/editor-plugin-synced-block/synced-block-plugin-type';

import { maybeParseNativeEmbedParams } from '@atlassian/native-embeds-core/services/client/iframe-integration/main';
import type { NativeEmbedChildCommand } from '@atlassian/native-embeds-core/types/commands/types';

import {
	generateSlug,
	buildContentPath,
	addQueryParams,
} from '@confluence/route-manager-utils/entry-points/url.ts';
// eslint-disable-next-line no-restricted-imports -- deprecated - use RouteState instead
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { useAccessLinkEditMode } from '@confluence/external-share-context/entry-points/AccessLinkEditMode.ts';
import { ExternalShareContext } from '@confluence/external-share-context/entry-points/ExternalShareContext.ts';
import { useNavigationPolicy } from '@confluence/route-manager/entry-points/useNavigationPolicy.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { ACCESS_LINK, COMPANY_HUB } from '@confluence/named-routes/entry-points/routes.ts';
import { FORCE_TRANSITION_QUERY_PARAM } from '@confluence/named-routes/entry-points/force-transition.ts';
import type { NativeCollabInjectedProps } from '@confluence/native-collab/entry-points/commonCollabTypes.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import type { DialogsStateContainer } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';

import { pagePublishedState, getClosePreloadUrl } from '../utils/index.ts';

import { useEditorBreadcrumbs } from './useEditorBreadcrumbs/index.ts';
import type { DraftSyncResponseError, UseDraftSyncProps } from './useDraftSync/useDraftSync.tsx';
import { useDraftSync } from './useDraftSync/useDraftSync.tsx';
import type { PublishProps, UsePublishProps } from './usePublish/usePublish.tsx';
import { usePublish } from './usePublish/usePublish.tsx';
import { useForceRefetchContent } from './useForceRefetchContent.ts';

type UseDraftSyncAndPublishProps = Omit<UseDraftSyncProps, 'isPublishingContent'> &
	Omit<UsePublishProps, 'isSavingDraft' | 'afterPublish' | 'saveDraftPromise'> &
	Pick<NativeCollabInjectedProps, 'title' | 'viewMode'> & {
		onCollabError: () => void;
		isUnpublishedDraft: boolean;
		flags?: FlagsStateContainer;
		dialogs?: DialogsStateContainer;
		editorPresetAPI?: PublicPluginAPI<[CollabEditPlugin, SyncedBlockPlugin]> | null;
		optimisticDeleteItemInTree?: (contentId: string) => void;
		sendNativeEmbedChildCommand?: (command: NativeEmbedChildCommand) => void;
	};

export enum ContentTypeType {
	PAGE = 'page',
	BLOGPOST = 'blogpost',
}

export const useDraftSyncAndPublish = ({
	contentId,
	contentType,
	refetchNativeCollab,
	editorActions,
	embeddedConfluenceSource,
	isFirstParticipant,
	isUnpublishedDraft,
	spaceKey,
	title,
	version,
	onCollabError,
	checkIfConditionPreventingPublish,
	flags,
	dialogs,
	editorPresetAPI,
	editorSessionId,
	onPublishUpdateTitleInPageTree,
	optimisticDeleteItemInTree,
	startingLinks,
	viewMode,
	sendNativeEmbedChildCommand,
	onDraftSyncSuccess,
}: UseDraftSyncAndPublishProps): {
	triggerSaveDraft: () => void;
	isSavingDraft: boolean;
	saveDraftError: DraftSyncResponseError | null;
	isPublishingContent: boolean;
	cancelDebouncedSaveDraft: () => void;
	onPublish: ({
		suppressNotifyWatchers,
		message,
		schedulePublishDate,
		ancestors,
		restrictions,
		retryOnConflict,
		newSpaceKey,
		moveRequest,
	}: PublishProps) => () => Promise<void>;
	navigateOnClose: (args?: { isDiscarding?: boolean }) => Promise<void>;
	saveDraft: ({ isOnClose }?: any) => Promise<void>;
} => {
	const _isSavingDraft = useRef<boolean>(false);
	const { push, match, matchRoute } = useContext(RoutesContext);
	const { routePolicy } = useNavigationPolicy();
	const forceRefetchContent = useForceRefetchContent(contentId, spaceKey);
	const pushWithRoutePolicy = useCallback(
		(url) => {
			const targetUrl = isCompanyHubSpaceKey(spaceKey) ? COMPANY_HUB.toUrl() : url;
			const route = matchRoute(targetUrl);

			if (
				routePolicy &&
				!routePolicy.willPushInSelf(match?.name, contentId, route?.name, route?.params?.contentId)
			) {
				return;
			}

			push(targetUrl);
		},
		[matchRoute, routePolicy, contentId, match, push, spaceKey],
	);

	const breadcrumbs = useEditorBreadcrumbs(contentId);

	// True only on the Access Link route, so this is a constant `false` everywhere else.
	const { isAvailable: canEditInPlace, exitEditMode } = useAccessLinkEditMode();
	const { shareToken } = useContext(ExternalShareContext);

	// Phase 2: Capture native embed state at mount time, before the SPA router
	// can strip the bridge query params (nativeEmbedChildId / nativeEmbedParentId)
	// from window.location.search during internal navigations.
	// Phase 1 (cc-maui-experiment) re-checked on every call; phase 2 captures once.
	const isNativeEmbedRef = useRef(
		!!maybeParseNativeEmbedParams(new URLSearchParams(window.location.search)) &&
			(expVal('cc-maui-experiment', 'isEnabled', false) ||
				// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
				fg('platform_native_embeds_rollout_non_maui_experience')),
	);

	const isNativeEmbed = () => {
		return (
			!!maybeParseNativeEmbedParams(new URLSearchParams(window.location.search)) &&
			(expVal('cc-maui-experiment', 'isEnabled', false) ||
				// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
				fg('platform_native_embeds_rollout_non_maui_experience'))
		);
	};

	const navigateOrSignalSwitchToViewing = useCallback(
		(url: string) => {
			// An Access Link viewer editing in place has nowhere to navigate to: `url` is a
			// space-scoped path they hold no permission for. Switch back to viewing instead,
			// as the native-embed branch below also does.
			if (canEditInPlace) {
				exitEditMode();

				// Dropping `url` also drops the forced transition `navigateOnPublish` put on it, and the
				// transition is what stops `useIsLivePageWithSSR` from reporting the SSR verdict for the
				// rest of the page load. Without it a live doc published back to a page keeps rendering
				// through `LivePageComponent` until the viewer reloads. Carry the param onto our own route.
				const forcedTransition = new URL(url, window.location.origin).searchParams.get(
					FORCE_TRANSITION_QUERY_PARAM,
				);
				if (forcedTransition && shareToken) {
					push(
						ACCESS_LINK.toUrl(
							{ shareToken },
							{ query: { [FORCE_TRANSITION_QUERY_PARAM]: forcedTransition } },
						),
					);
				}
				return;
			}

			const nativeEmbedDetected =
				// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
				fg('cc-maui-phase-2') || fg('platform_native_embeds_rollout_non_maui_experience')
					? isNativeEmbedRef.current
					: isNativeEmbed();
			if (nativeEmbedDetected && sendNativeEmbedChildCommand) {
				sendNativeEmbedChildCommand({ type: 'switch-to-viewing' });
			} else {
				pushWithRoutePolicy(url);
			}
		},
		[
			canEditInPlace,
			exitEditMode,
			sendNativeEmbedChildCommand,
			pushWithRoutePolicy,
			push,
			shareToken,
		],
	);

	const navigateOnClose = useCallback(
		async (args?: { isDiscarding?: boolean }) => {
			// Refetch content to resolve stale cache issues
			await forceRefetchContent();

			// The `isDiscarding` check here prevents a race condition with the page tree refresh code
			// triggered when discarding a blank draft (in EditorPrimaryToolbarComponent.tsx).
			if (isUnpublishedDraft && !args?.isDiscarding) {
				onPublishUpdateTitleInPageTree?.();
			}

			const url = getClosePreloadUrl({
				contentId,
				spaceKey,
				contentType,
				breadcrumbs,
				isUnpublishedDraft,
			});

			navigateOrSignalSwitchToViewing(url);
		},
		[
			isUnpublishedDraft,
			contentType,
			breadcrumbs,
			spaceKey,
			contentId,
			forceRefetchContent,
			navigateOrSignalSwitchToViewing,
			onPublishUpdateTitleInPageTree,
		],
	);

	const navigateOnPublish = useCallback(
		({ res, newSpaceKey, isLivePagePublish }) => {
			let url = buildContentPath(
				newSpaceKey || spaceKey,
				contentId,
				contentType,
				res.createdDate,
				generateSlug(title),
			);

			// SSR rendered live pages that are published must be treated as a transition manually like this. If we don't, ViewPageCommon may erronously assume the just-published page is still a live doc and render it incorrectly as LivePageComponent
			if (window.__SSR_RENDERED__ && isLivePagePublish) {
				url = addQueryParams(url, { [FORCE_TRANSITION_QUERY_PARAM]: uuid() });
			}

			pagePublishedState.pageJustPublished = true;
			navigateOrSignalSwitchToViewing(url);
		},
		[spaceKey, contentId, contentType, title, navigateOrSignalSwitchToViewing],
	);

	// Draft Sync Handling
	const {
		triggerSaveDraft,
		isSavingDraft,
		saveDraft,
		saveDraftPromise,
		cancelDebouncedSaveDraft,
		saveDraftError,
	} = useDraftSync({
		contentId,
		editorActions,
		contentType,
		isFirstParticipant,
		onCollabError,
		editorPresetAPI,
		editorSessionId,
		viewMode,
		onDraftSyncSuccess,
	});
	useEffect(() => {
		_isSavingDraft.current = isSavingDraft;
	}, [isSavingDraft]);

	const afterPublish = useCallback(
		({ res, newSpaceKey, isLivePagePublish }) => {
			cancelDebouncedSaveDraft();
			onPublishUpdateTitleInPageTree?.();
			navigateOnPublish({ res, newSpaceKey, isLivePagePublish });
		},
		[cancelDebouncedSaveDraft, onPublishUpdateTitleInPageTree, navigateOnPublish],
	);

	// Publish Handling
	const { isPublishingContent, onPublish } = usePublish({
		contentId,
		contentType,
		embeddedConfluenceSource,
		refetchNativeCollab,
		editorActions,
		spaceKey,
		afterPublish,
		version,
		onCollabError,
		saveDraftPromise,
		checkIfConditionPreventingPublish,
		flags,
		dialogs,
		startingLinks,
		optimisticDeleteItemInTree,
		editorPresetAPI,
	});

	return useMemo(
		() => ({
			triggerSaveDraft,
			isSavingDraft,
			saveDraftError,
			isPublishingContent,
			cancelDebouncedSaveDraft,
			onPublish,
			navigateOnClose,
			saveDraft,
		}),
		[
			triggerSaveDraft,
			isSavingDraft,
			saveDraftError,
			isPublishingContent,
			cancelDebouncedSaveDraft,
			onPublish,
			navigateOnClose,
			saveDraft,
		],
	);
};
